<template>
  <div class="w-full relative">
    <!-- Desktop button -->
    <div class="hidden md:inline-flex">
      <BaseButton color="primary" @click="cart.open()">
        <CartIcon class="h-6 w-6 mr-2 fill-current" />
        {{ $t('globals.cart') }}

        <span
          v-if="ticketCount > 0"
          class="absolute top-0 right-0 border-surface-light border-2 flex items-center justify-center translate-x-1/4 -translate-y-1/4 bg-primary text-primary-text w-7 h-7 rounded-full px-2 py-1 text-xs font-semibold"
        >
          {{ ticketCount }}
        </span>
      </BaseButton>
    </div>

    <!-- Mobile button -->
    <button
      class="bg-primary rounded-full items-center justify-center w-12 h-12 flex md:hidden"
      @click="cart.open()"
    >
      <CartIcon class="h-6 w-6 fill-primary-text md:mr-2" />
      <span class="sr-only">
        {{ $t('globals.cart') }}
      </span>
      <span
        v-if="ticketCount > 0"
        class="absolute top-0 right-0 border-surface-light border-2 flex items-center justify-center translate-x-1/4 -translate-y-1/4 bg-primary text-primary-text w-7 h-7 rounded-full px-2 py-1 text-xs font-semibold"
      >
        {{ ticketCount }}
      </span>
    </button>

    <transition name="fade">
      <div
        v-if="cart.isOpen"
        @click="cart.close()"
        class="fixed cursor-pointer hover:opacity-80 inset-0 bg-black/50 transition-opacity duration-300"
      ></div>
    </transition>

    <BaseSlideDrawer v-model="cart.isOpen" title="Din kurv">
      <template #belowHeader v-if="activeExpire > 0">
        <div class="w-full flex grow items-center gap-1 font-semibold bg-surface-grey-light text-sm border-t border-surface-grey px-4 py-2" >
          <StopwatchIcon class="fill-primary w-6 h-6" />
          <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
        </div>
      </template>
      <div class="space-y-4">
        <div
          class="p-4 bg-surface-grey-light rounded-md"
          v-for="item in cart.items"
          :key="item.id"
        >
          <CartListItem :item="item" />
        </div>

        <div
          v-if="!ticketCount"
          class="bg-surface-grey-light p-4 rounded-md flex gap-4 font-semibold"
        >
          <CartIcon class="h-6 w-6 fill-primary" />
          {{ $t('globals.cart_empty') }}
        </div>
      </div>

      <template #footer v-if="ticketCount > 0">
        <CartSummaryTable />
        <BaseButton
          class="w-full mt-4"
          color="primary"
          size="lg"
          :to="checkoutLink"
          @click="handleContinueToCheckout"
        >
          {{ $t('globals.continue_to_checkout') }}
          <ArrowRightIcon class="fill-current h-4 w-4 ml-2" />
        </BaseButton>
      </template>
    </BaseSlideDrawer>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  import CartListItem from '@/components/checkout/CheckoutListItem.vue';
  import CartSummaryTable from '@/components/checkout/CheckoutSummaryTable.vue';
  import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { slugify } from '@/utils/slugify';

  import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
  import CartIcon from '../icons/CartIcon.vue';
  import BaseButton from '../ui/BaseButton.vue';
  import BaseCountdown from '../ui/BaseCountdown.vue';
  import BaseSlideDrawer from '../ui/BaseSlideDrawer.vue';

  const cart = useCartStore();
  const checkoutStore = useCheckoutStore();
  const route = useRoute();

  const ticketCount = computed(() =>
    cart.items.reduce((sum, item) => sum + item.tickets.reduce((s, t) => s + t.count, 0), 0),
  );

  const activeExpire = computed(() =>
    route.name === 'checkout-payment' ? cart.paymentFailedExpire : cart.cartExpire
  );

  const checkoutLink = computed(() => {
    const firstItem = cart.items[0];
    if (!firstItem) return '/';
    const isClosedEvent = typeof firstItem.id === 'string' && /^[0-9a-f]{96}$/.test(firstItem.id);
    if (isClosedEvent) return `/${firstItem.id}/tickets`;
    return `/${firstItem.id}/${slugify(firstItem.name)}/tickets`;
  });

  function handleContinueToCheckout() {
    const firstItem = cart.items[0];
    if (firstItem) {
      checkoutStore.setCheckoutID(firstItem.id);
    }
    cart.close();
  }
</script>
